import { TFunction } from 'next-i18next'
import { DistanceOrElevationLogoEnum } from 'components/RacePage/RaceDistancesAndElevations/DistanceOrElevationLogo'
import { RaceLiveEvent } from 'domain/models/EventRead/LiveEvent'
import { recordKeys } from 'helpers/records'

export const runningLevel2Categories = [
  'LESS_THAN_5_KM',
  'KM5',
  'KM10',
  'KM15',
  'KM20',
  'HALF_MARATHON',
  'MARATHON',
  'EKIDEN',
  'ULTRA_RUNNING',
  'CROSS'
] as const

export const trailLevel2Categories = [
  'DISCOVERY_TRAIL',
  'SHORT_TRAIL',
  'LONG_TRAIL',
  'ULTRA_TRAIL',
  'VERTICAL_KILOMETER'
] as const

export const triathlonLevel2Categories = [
  'AQUATHLON',
  'DUATHLON',
  'TRIATHLON_XS',
  'TRIATHLON_S',
  'TRIATHLON_M',
  'TRIATHLON_L',
  'TRIATHLON_XXL',
  'TRIATHLON_KIDS',
  'ULTRA_TRIATHLON',
  'CROSS_TRIATHLON',
  'RUN_BIKE',
  'SWIM_RUN',
  'SWIM_BIKE'
] as const

export const funLevel2Categories = [
  'OBSTACLE_RACE',
  'SPARTAN_RACE',
  'COLOR_RUN',
  'MUD_DAY'
] as const

export const cyclingLevel2Categories = [
  'ULTRA_CYCLING',
  'GRAN_FONDO',
  'ROAD_CYCLING_TOUR',
  'CYCLE_TOURING',
  'TIME_TRIAL',
  'BIKEPACKING',
  'GRAVEL_RACE',
  'GRAVEL_RIDE',
  'MOUNTAIN_BIKE_RIDE',
  'XC_MOUNTAIN_BIKE',
  'ENDURO_MOUNTAIN_BIKE'
] as const

const otherLevel2Categories = [
  'YOGA',
  'FREE_FLIGHT',
  'RAID',
  'ORIENTEERING',
  'BIATHLON',
  'CROSS_COUNTRY_SKIING',
  'SWIMMING',
  'CANICROSS',
  'HYROX'
] as const

export const walkLevel2Categories = ['HIKING', 'NORDIC_WALK'] as const

export enum DistanceOrElevationUnit {
  km = 'km',
  m = 'm',
  years = 'ans'
}

export enum DistanceOrElevationTranslationKey {
  positiveElevation,
  negativeElevation,
  walking,
  running,
  swimming,
  biking,
  runAndBike,
  runAndSwim,
  age
}

export const raceCategoriesLevel1 = {
  RUNNING: {
    level2Categories: runningLevel2Categories,
    label: 'Running',
    prefix: 'ALL_FEM',
    url: 'running',
    key: 'RUNNING'
  },
  TRAIL: {
    level2Categories: trailLevel2Categories,
    label: 'Trail',
    prefix: 'ALL_MAN',
    url: 'trail',
    key: 'TRAIL'
  },
  TRIATHLON: {
    level2Categories: triathlonLevel2Categories,
    label: 'Triathlon',
    prefix: 'ALL_MAN',
    url: 'triathlon',
    key: 'TRIATHLON'
  },
  FUN: {
    level2Categories: funLevel2Categories,
    label: 'Course Fun',
    prefix: 'ALL_FEM',
    url: 'fun',
    key: 'FUN'
  },
  WALK: {
    level2Categories: walkLevel2Categories,
    label: 'Marche',
    prefix: 'ALL_FEM',
    url: 'marche',
    key: 'WALK'
  },
  CYCLING: {
    level2Categories: cyclingLevel2Categories,
    label: 'Vélo',
    prefix: 'ALL_FEM',
    url: 'velo',
    key: 'CYCLING'
  },
  OTHER: {
    level2Categories: otherLevel2Categories,
    label: 'Autre',
    prefix: 'ALL_FEM',
    url: 'autre',
    key: 'OTHER'
  }
} as const

export const isLevel1Category = (
  param: string
): param is keyof typeof raceCategoriesLevel1 =>
  recordKeys<string, unknown>(raceCategoriesLevel1).includes(param)

export const urlParser = (param: string) => {
  if (!param) return ''
  const parsed = param
    .replace(/[ÀÁÂÃÄÅ]/g, 'A')
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[ČÇĆ]/g, 'C')
    .replace(/[čçć]/g, 'c')
    .replace(/[ÈÉÊË]/g, 'E')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ÍÌÎÏ]/g, 'I')
    .replace(/[íìîï]/g, 'i')
    .replace(/[ŇÑ]/g, 'N')
    .replace(/[ňñ]/g, 'n')
    .replace(/[ÓÖÒÔÕ]/g, 'O')
    .replace(/[óöòôõøð]/g, 'o')
    .replace(/[ÚŮÜÙÛ]/g, 'U')
    .replace(/[úůüùû]/g, 'u')
    .replace(/[ ·/_,:;']/g, '-')
  if (parsed.toLowerCase() === 'enfants') return 'pour-enfant'
  return parsed.toLowerCase()
}

export const allLevel2Categories = [
  ...runningLevel2Categories,
  ...trailLevel2Categories,
  ...triathlonLevel2Categories,
  ...funLevel2Categories,
  ...walkLevel2Categories,
  ...cyclingLevel2Categories,
  ...otherLevel2Categories
]

export type AllLevel2Categories = (typeof allLevel2Categories)[number]

export const isLevel2Category = (param: string): param is AllLevel2Categories =>
  (allLevel2Categories as readonly string[]).includes(param)

export const getRaceCategory1Label = (param: string) =>
  isLevel1Category(param) ? raceCategoriesLevel1[param].label : ''

export const getRaceCategory2Label = (param: string, t: TFunction) =>
  isLevel2Category(param) ? t(param) : ''

export interface DistanceOrElevationInfo {
  key: string
  field: string
  translationKey: DistanceOrElevationTranslationKey
  value: number | string | undefined
  unit: DistanceOrElevationUnit
  icons: DistanceOrElevationLogoEnum[]
}
export const getDistancesAndElevationsInfoFromCategoryLevels = (
  categoryLevel1: RaceLiveEvent['categoryLevel1'],
  categoryLevel2: RaceLiveEvent['categoryLevel2'],
  race?: RaceLiveEvent
): DistanceOrElevationInfo[] => {
  switch (categoryLevel1) {
    case 'RUNNING':
    case 'TRAIL':
      return [
        {
          key: 'Distance',
          field: 'runDistance',
          translationKey: DistanceOrElevationTranslationKey.running,
          value: race?.runDistance,
          unit: DistanceOrElevationUnit.km,
          icons: [DistanceOrElevationLogoEnum.RUN]
        },
        {
          key: 'D+',
          field: 'runPositiveElevation',
          translationKey: DistanceOrElevationTranslationKey.positiveElevation,
          value: race?.runPositiveElevation ?? undefined,
          unit: DistanceOrElevationUnit.m,
          icons: [DistanceOrElevationLogoEnum.D_PLUS]
        },
        {
          key: 'D-',
          field: 'runNegativeElevation',
          translationKey: DistanceOrElevationTranslationKey.negativeElevation,
          value: race?.runNegativeElevation ?? undefined,
          unit: DistanceOrElevationUnit.m,
          icons: [DistanceOrElevationLogoEnum.D_MINUS]
        }
      ]
    case 'FUN':
      return [
        {
          key: 'Distance',
          field: 'runDistance',
          translationKey: DistanceOrElevationTranslationKey.running,
          value: race?.runDistance,
          unit: DistanceOrElevationUnit.km,
          icons: [DistanceOrElevationLogoEnum.RUN]
        }
      ]
    case 'WALK':
      return [
        {
          key: 'Distance',
          field: 'walkDistance',
          translationKey: DistanceOrElevationTranslationKey.walking,
          value: race?.walkDistance,
          unit: DistanceOrElevationUnit.km,
          icons: [
            categoryLevel2 === 'NORDIC_WALK'
              ? DistanceOrElevationLogoEnum.NORDIC_HIKE
              : DistanceOrElevationLogoEnum.HIKE
          ]
        },
        {
          key: 'D+',
          field: 'walkPositiveElevation',
          translationKey: DistanceOrElevationTranslationKey.positiveElevation,
          value: race?.walkPositiveElevation ?? undefined,
          unit: DistanceOrElevationUnit.m,
          icons: [DistanceOrElevationLogoEnum.D_PLUS]
        },
        {
          key: 'D-',
          field: 'walkNegativeElevation',
          translationKey: DistanceOrElevationTranslationKey.negativeElevation,
          value: race?.walkNegativeElevation ?? undefined,
          unit: DistanceOrElevationUnit.m,
          icons: [DistanceOrElevationLogoEnum.D_MINUS]
        }
      ]
    case 'CYCLING':
      return [
        {
          key: 'Distance',
          field: 'bikeDistance',
          translationKey: DistanceOrElevationTranslationKey.biking,
          value: race?.bikeDistance,
          unit: DistanceOrElevationUnit.km,
          icons: [DistanceOrElevationLogoEnum.BIKE]
        },
        {
          key: 'D+',
          field: 'bikePositiveElevation',
          translationKey: DistanceOrElevationTranslationKey.positiveElevation,
          value: race?.bikePositiveElevation ?? undefined,
          unit: DistanceOrElevationUnit.m,
          icons: [DistanceOrElevationLogoEnum.D_PLUS]
        },
        {
          key: 'D-',
          field: 'bikeNegativeElevation',
          translationKey: DistanceOrElevationTranslationKey.negativeElevation,
          value: race?.bikeNegativeElevation ?? undefined,
          unit: DistanceOrElevationUnit.m,
          icons: [DistanceOrElevationLogoEnum.D_MINUS]
        }
      ]
    case 'TRIATHLON':
      switch (categoryLevel2) {
        case 'AQUATHLON':
          return [
            {
              key: 'Swim',
              field: 'swimDistance',
              translationKey: DistanceOrElevationTranslationKey.swimming,
              value: race?.swimDistance,
              unit: DistanceOrElevationUnit.m,
              icons: [DistanceOrElevationLogoEnum.SWIM]
            },
            {
              key: 'Run',
              field: 'runDistance',
              translationKey: DistanceOrElevationTranslationKey.running,
              value: race?.runDistance,
              unit: DistanceOrElevationUnit.km,
              icons: [DistanceOrElevationLogoEnum.RUN]
            }
          ]
        case 'DUATHLON':
          return [
            {
              key: 'Run',
              field: 'runDistance',
              translationKey: DistanceOrElevationTranslationKey.running,
              value: race?.runDistance,
              unit: DistanceOrElevationUnit.km,
              icons: [DistanceOrElevationLogoEnum.RUN]
            },
            {
              key: 'Bike',
              field: 'bikeDistance',
              translationKey: DistanceOrElevationTranslationKey.biking,
              value: race?.bikeDistance,
              unit: DistanceOrElevationUnit.km,
              icons: [DistanceOrElevationLogoEnum.BIKE]
            },
            {
              key: 'Run',
              field: 'runDistance2',
              translationKey: DistanceOrElevationTranslationKey.running,
              value: race?.runDistance2,
              unit: DistanceOrElevationUnit.km,
              icons: [DistanceOrElevationLogoEnum.RUN]
            }
          ]
        case 'SWIM_RUN':
          return [
            {
              key: 'Swimrun',
              field: 'swimRunDistance',
              translationKey: DistanceOrElevationTranslationKey.runAndSwim,
              value: race?.swimRunDistance,
              unit: DistanceOrElevationUnit.km,
              icons: [
                DistanceOrElevationLogoEnum.SWIM,
                DistanceOrElevationLogoEnum.RUN
              ]
            }
          ]
        case 'SWIM_BIKE':
          return [
            {
              key: 'Swim',
              field: 'swimDistance',
              translationKey: DistanceOrElevationTranslationKey.swimming,
              value: race?.swimDistance,
              unit: DistanceOrElevationUnit.m,
              icons: [DistanceOrElevationLogoEnum.SWIM]
            },
            {
              key: 'Bike',
              field: 'bikeDistance',
              translationKey: DistanceOrElevationTranslationKey.biking,
              value: race?.bikeDistance,
              unit: DistanceOrElevationUnit.km,
              icons: [DistanceOrElevationLogoEnum.BIKE]
            }
          ]
        case 'RUN_BIKE':
          return [
            {
              key: 'Bike & Run',
              field: 'bikeRunDistance',
              translationKey: DistanceOrElevationTranslationKey.runAndBike,
              value: race?.bikeRunDistance,
              unit: DistanceOrElevationUnit.km,
              icons: [
                DistanceOrElevationLogoEnum.BIKE,
                DistanceOrElevationLogoEnum.RUN
              ]
            }
          ]
        default:
          return [
            {
              key: 'Swim',
              field: 'swimDistance',
              translationKey: DistanceOrElevationTranslationKey.swimming,
              value: race?.swimDistance,
              unit: DistanceOrElevationUnit.m,
              icons: [DistanceOrElevationLogoEnum.SWIM]
            },
            {
              key: 'Bike',
              field: 'bikeDistance',
              translationKey: DistanceOrElevationTranslationKey.biking,
              value: race?.bikeDistance,
              unit: DistanceOrElevationUnit.km,
              icons: [DistanceOrElevationLogoEnum.BIKE]
            },
            {
              key: 'Run',
              field: 'runDistance',
              translationKey: DistanceOrElevationTranslationKey.running,
              value: race?.runDistance,
              unit: DistanceOrElevationUnit.km,
              icons: [DistanceOrElevationLogoEnum.RUN]
            }
          ]
      }
    default:
      return []
  }
}
